<template>
  <div class="inv-page">

    <!-- Top action bar -->
    <div class="inv-topbar" id="printpagebutton">
      <button class="btn-back" @click="goBack">
        <i class="fa fa-arrow-left"></i> Back
      </button>
      <button class="btn-print" @click="printInvoice">
        <i class="fa fa-print"></i> Print Invoice
      </button>
    </div>

    <!-- Loading -->
    <div class="inv-loading" v-if="!invoiceDetails">
      <i class="fa fa-spinner fa-spin"></i> Loading invoice…
    </div>

    <!-- Invoice document -->
    <div class="inv-doc" id="printMe" v-if="invoiceDetails">

      <!-- ── Document Header ── -->
      <div class="inv-doc-header">
        <div class="inv-brand">
          <div class="inv-title">INVOICE</div>
          <div class="inv-no-badge"># {{ invoiceDetails.invoice_no }}</div>
        </div>
        <div class="inv-from">
          <div class="inv-from-name">{{ invoiceDetails.service_provider.name }}</div>
          <div class="inv-from-addr">{{ invoiceDetails.service_provider.full_address }}</div>
        </div>
      </div>

      <!-- ── Meta row ── -->
      <div class="inv-meta-row">
        <div class="inv-meta-cell">
          <div class="inv-meta-label">Billed To</div>
          <div class="inv-meta-value">
            {{ invoiceDetails.enrollment.fname }}
            {{ invoiceDetails.enrollment.mname ? invoiceDetails.enrollment.mname + ' ' : '' }}
            {{ invoiceDetails.enrollment.lname }}
          </div>
          <div class="inv-meta-sub">
            {{ invoiceDetails.enrollment.address1 }}{{ invoiceDetails.enrollment.address2 ? ', ' + invoiceDetails.enrollment.address2 : '' }},
            {{ invoiceDetails.enrollment.city }}, {{ invoiceDetails.enrollment.zipcode }}
          </div>
        </div>
        <div class="inv-meta-cell">
          <div class="inv-meta-label">Invoice Date</div>
          <div class="inv-meta-value">{{ invoiceDetails.created_at | dateFormat }}</div>
          <div class="inv-meta-sub">Updated {{ invoiceDetails.updated_at | dateFormatTime }}</div>
        </div>
        <div class="inv-meta-cell">
          <div class="inv-meta-label">Appointment No.</div>
          <div class="inv-meta-value mono">{{ invoiceDetails.enrollment.appointment_id }}</div>
        </div>
        <div class="inv-meta-cell">
          <div class="inv-meta-label">Enrollment No.</div>
          <div class="inv-meta-value mono">{{ invoiceDetails.enrollment.id }}</div>
        </div>
      </div>

      <!-- ── Line Items ── -->
      <div class="inv-section">
        <table class="inv-table">
          <thead>
            <tr>
              <th class="th-desc">Description</th>
              <th class="th-num">Qty</th>
              <th class="th-num">Rate</th>
              <th class="th-num">Tax</th>
              <th class="th-num th-amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in invoiceDetails.invoice_detail" :key="index"
              :class="{ 'row-discount': index > 0 && !item.is_additional && item.rate < 0 }">

              <!-- Description -->
              <td class="td-desc">
                <template v-if="item.title && item.title !== ''">
                  {{ item.title }}
                </template>
                <template v-else-if="index > 0 && !item.is_additional && item.rate < 0">
                  <span class="discount-label"><i class="fa fa-tag"></i> Promocode Discount</span>
                </template>
                <template v-else-if="index > 0 && !item.is_additional">
                  {{
                    invoiceDetails.enrollment.archival_period_detail.name === 'Archival'
                      ? 'Archival'
                      : invoiceDetails.enrollment.archival_period_detail.name + ' (Archival)'
                  }}
                </template>
                <template v-else>
                  {{ item.is_additional ? 'Additional Service' : invoiceDetails.service_name }}
                </template>
              </td>

              <!-- Qty -->
              <td class="td-num">
                <template v-if="!(index > 0 && !item.is_additional && item.rate < 0)">
                  {{ item.qty }}
                </template>
              </td>

              <!-- Rate -->
              <td class="td-num">
                <template v-if="!(index > 0 && !item.is_additional && item.rate < 0)">
                  ${{ item.rate | toFixedTwoDigit }}
                </template>
              </td>

              <!-- Tax -->
              <td class="td-num">
                <template v-if="!(index > 0 && !item.is_additional && item.rate < 0)">
                  ${{ item.tax_amount | toFixedTwoDigit }}
                </template>
              </td>

              <!-- Amount -->
              <td class="td-num td-amount">
                <template v-if="item.is_additional">
                  ${{ (parseInt(item.qty) * parseFloat(item.rate) + parseFloat(item.tax_amount)) | toFixedTwoDigit }}
                </template>
                <template v-else-if="index > 0 && !item.is_additional && item.rate < 0">
                  <span class="discount-amount">
                    − ${{ (parseInt(item.qty) * parseFloat(item.rate) * -1 + parseFloat(item.tax_amount)) | toFixedTwoDigit }}
                  </span>
                </template>
                <template v-else>
                  ${{ (parseFloat(item.rate) * parseInt(item.qty) + parseFloat(item.tax_amount)) | toFixedTwoDigit }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── Footer: payment + total ── -->
      <div class="inv-footer-row">

        <!-- Payment Details -->
        <div class="pay-block">
          <div class="pay-block-title">Payment Details</div>

          <!-- Credit Card -->
          <template v-if="invoiceDetails.payment_type === 'C'">
            <div class="pay-row">
              <span class="pay-label">Type</span>
              <span class="pay-value">
                <i class="fa fa-credit-card"></i> Credit Card
              </span>
            </div>
            <div class="pay-row">
              <span class="pay-label">Status</span>
              <span class="pay-badge" :class="invoiceDetails.payment_status === 'Refund' ? 'badge-refund' : 'badge-paid'">
                {{ invoiceDetails.payment_status === 'Refund' ? 'Refunded' : 'Paid' }}
              </span>
            </div>
          </template>

          <!-- On-site -->
          <template v-if="invoiceDetails.payment_type === 'O'">
            <div class="pay-row">
              <span class="pay-label">Type</span>
              <span class="pay-value"><i class="fa fa-store"></i> On-site Payment</span>
            </div>
            <div class="pay-row">
              <span class="pay-label">Status</span>
              <span class="pay-badge badge-onsite">Collect on-site</span>
            </div>
          </template>

          <!-- Company Payment -->
          <template v-if="invoiceDetails.payment_type === 'CP'">
            <div class="pay-row">
              <span class="pay-label">Type</span>
              <span class="pay-value"><i class="fa fa-building"></i> Company Payment</span>
            </div>
            <div class="pay-row">
              <span class="pay-label">Status</span>
              <span class="pay-badge badge-company">Bill to Company</span>
            </div>
            <div class="pay-divider"></div>
            <div class="pay-block-title">Company Details</div>
            <div class="pay-row">
              <span class="pay-label">Name</span>
              <span class="pay-value">{{ invoiceDetails.enrollment.job_client_name }}</span>
            </div>
            <div class="pay-row">
              <span class="pay-label">Address</span>
              <span class="pay-value">{{ invoiceDetails.enrollment.job_client_address }}</span>
            </div>
          </template>

          <!-- Abbreviation -->
          <template v-if="invoiceDetails.payment_type === 'A'">
            <div class="pay-row">
              <span class="pay-label">Type</span>
              <span class="pay-value"><i class="fa fa-tag"></i> Abbreviation</span>
            </div>
            <div class="pay-row">
              <span class="pay-label">Status</span>
              <span class="pay-badge badge-company">Bill to Company</span>
            </div>
            <div class="pay-divider"></div>
            <div class="pay-block-title">Company Details</div>
            <div class="pay-row">
              <span class="pay-label">Code</span>
              <span class="pay-value mono">{{ invoiceDetails.enrollment.company.abbreviation_code }}</span>
            </div>
            <div class="pay-row">
              <span class="pay-label">Name</span>
              <span class="pay-value">{{ invoiceDetails.enrollment.company.company_name }}</span>
            </div>
            <div class="pay-row">
              <span class="pay-label">Address</span>
              <span class="pay-value">{{ invoiceDetails.enrollment.company.address }}</span>
            </div>
          </template>
        </div>

        <!-- Total Summary -->
        <div class="total-block">
          <div class="total-label">Total Amount</div>
          <div class="total-amount">${{ invoiceDetails.total | toFixedTwoDigit }}</div>
          <div class="total-note">Taxes included</div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import { FETCH_ENROLLMENT_INVOICE_DETAILS } from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import moment from "moment";

export default {
  filters: {
    dateFormat(date) { return moment(date).format("MMM DD, YYYY"); },
    dateFormatTime(date) { return moment(date).format("MMM DD, YYYY HH:mm"); },
    toFixedTwoDigit(amount) { return parseFloat(amount).toFixed(2); }
  },
  data() {
    return {
      invoiceDetails: null,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Invoice Details", route: "" }
      ]
    };
  },
  mounted() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getInvoiceDetails();
  },
  methods: {
    printInvoice() {
      const hide = ["printpagebutton", "kt_subheader", "kt_header", "kt_copyright_footer", "kt_aside_parent"];
      hide.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.visibility = "hidden";
      });
      document.body.classList.add("aside-minimize");
      window.print();
      hide.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.visibility = "visible";
      });
      document.body.classList.remove("aside-minimize");
    },
    goBack() { this.$router.go(-1); },
    getInvoiceDetails() {
      this.$store.dispatch(FETCH_ENROLLMENT_INVOICE_DETAILS, { id: this.$route.params.enrollment_id })
        .then(res => { this.invoiceDetails = res.data; });
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #e4e9f2;

.inv-page {
  padding: 8px 0 60px;
  font-family: $font;
  max-width: 960px;
  margin: 0 auto;
}

/* ── Top bar ── */
.inv-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1.5px solid $border;
  background: #fff;
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { border-color: $blue; color: $blue; }
}

.btn-print {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 18px;
  border-radius: 8px;
  border: none;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
}

/* ── Loading ── */
.inv-loading {
  text-align: center;
  padding: 60px 24px;
  color: #a0aab8;
  font-size: 13px;
  i { margin-right: 8px; }
}

/* ── Invoice document ── */
.inv-doc {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(13, 27, 62, 0.09);
  overflow: hidden;
}

/* ── Document header ── */
.inv-doc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 32px 36px 24px;
  background: linear-gradient(135deg, #0d1b3e 0%, #1a2f5e 100%);
  gap: 20px;
  flex-wrap: wrap;
}

.inv-brand {
  display: flex;
  align-items: center;
  gap: 14px;
}

.inv-title {
  font-size: 32px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 2px;
}

.inv-no-badge {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.25);
  color: rgba(255,255,255,0.85);
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  align-self: center;
}

.inv-from {
  text-align: right;
}

.inv-from-name {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 4px;
}

.inv-from-addr {
  font-size: 12px;
  color: rgba(255,255,255,0.55);
  max-width: 280px;
  line-height: 1.5;
  text-align: right;
}

/* ── Meta row ── */
.inv-meta-row {
  display: flex;
  gap: 0;
  border-bottom: 1px solid $border;
  background: #f8fafd;
}

.inv-meta-cell {
  flex: 1;
  padding: 18px 24px;
  border-right: 1px solid $border;

  &:last-child { border-right: none; }
}

.inv-meta-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #a0aab8;
  margin-bottom: 5px;
}

.inv-meta-value {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
  line-height: 1.4;

  &.mono { font-family: 'Courier New', monospace; font-size: 13px; }
}

.inv-meta-sub {
  font-size: 11px;
  color: #7a8499;
  margin-top: 3px;
  line-height: 1.4;
}

/* ── Line items ── */
.inv-section {
  padding: 24px 36px;
  border-bottom: 1px solid $border;
}

.inv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;

  thead tr {
    border-bottom: 2px solid #eef0f8;

    th {
      padding: 8px 12px;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #a0aab8;
      background: #fff;
    }
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;

    &:last-child { border-bottom: none; }

    &.row-discount td {
      background: #fffbf0;
    }

    td { padding: 12px; vertical-align: middle; color: $dark; }
  }
}

.th-desc  { text-align: left; }
.th-num   { text-align: right; }
.th-amount { padding-right: 0 !important; }

.td-desc { color: $dark; font-weight: 500; }
.td-num  { text-align: right; color: #4a5568; }
.td-amount { font-weight: 700; color: $dark; padding-right: 0 !important; }

.discount-label {
  color: #c05621;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  i { font-size: 11px; }
}

.discount-amount { color: #c05621; font-weight: 700; }

/* ── Footer row ── */
.inv-footer-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 28px 36px;
  gap: 32px;
  flex-wrap: wrap;
}

/* Payment block */
.pay-block {
  flex: 1;
  min-width: 220px;
  max-width: 380px;
}

.pay-block-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #a0aab8;
  margin-bottom: 12px;
}

.pay-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 13px;
}

.pay-label {
  font-size: 11px;
  color: #a0aab8;
  min-width: 60px;
  flex-shrink: 0;
}

.pay-value {
  font-weight: 500;
  color: $dark;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  i { color: $blue; font-size: 12px; }

  &.mono { font-family: 'Courier New', monospace; font-size: 12px; }
}

.pay-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;

  &.badge-paid    { background: #dcfce7; color: #166534; }
  &.badge-refund  { background: #ede9fe; color: #5b21b6; }
  &.badge-onsite  { background: #fef3c7; color: #92400e; }
  &.badge-company { background: #dbeafe; color: #1e40af; }
}

.pay-divider {
  border-top: 1px solid $border;
  margin: 12px 0;
}

/* Total block */
.total-block {
  background: linear-gradient(135deg, #0d1b3e 0%, #1a2f5e 100%);
  border-radius: 12px;
  padding: 24px 32px;
  text-align: center;
  min-width: 200px;
}

.total-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255,255,255,0.55);
  margin-bottom: 8px;
}

.total-amount {
  font-size: 36px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  margin-bottom: 6px;
}

.total-note {
  font-size: 11px;
  color: rgba(255,255,255,0.4);
}

/* ── Print styles ── */
@media print {
  .inv-topbar { display: none !important; }
  .inv-page { max-width: 100%; padding: 0; }
  .inv-doc { box-shadow: none; border-radius: 0; }
}
</style>
